import { NextPage } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAuth } from '@/modules/auth/hooks/useAuth';
import { useGetFlow } from '@/modules/flow/hooks/useFlow';
import { BackForward } from '@/modules/player/presentation/atoms/BackForward';
import { Next } from '@/modules/player/presentation/atoms/Next';
import { PlayPause } from '@/modules/player/presentation/atoms/PlayPause';
import { ProgressBar } from '@/modules/player/presentation/molecules/ProgressBar';
import { usePlayer } from '@/modules/player/providers/PlayerProvider';
import { usePlayerPlayContext } from '@/modules/player/providers/dispatch/usePlayerPlayContext';
import { usePlayerStartMedia } from '@/modules/player/providers/dispatch/usePlayerStartMedia';
import { useQueueList } from '@/modules/player/providers/dispatch/useQueueList';
import { ProgramFollow } from '@/modules/programs/presentation/ProgramFollow';
import { Media } from '@/types';

const FlowPage: NextPage = () => {
  const { user } = useAuth();
  const { formatMessage } = useIntl();
  const { data, isLoading } = useGetFlow();
  const [currentItem, setCurrentItem] = useState<Media | null>(null);
  const { currentMedia, playContext, playing } = usePlayer();
  const { setQueueList } = useQueueList();
  const { startMedia } = usePlayerStartMedia();
  const { setPlayContext } = usePlayerPlayContext();

  useEffect(() => {
    if (!data) {
      return;
    }

    const currentItem = playContext === 'flow' ? currentMedia ?? data[0] : data[0];
    setCurrentItem(currentItem);

    // If something is already playing, the flow is authorized to automatically switch
    // context and start playing its own content.
    if (!playing || playContext === 'flow') {
      return;
    }

    setQueueList(data);
    startMedia({ media: currentItem, autoPlay: true });
    setPlayContext('flow');
  }, [data, playContext, currentMedia, playing, setQueueList, startMedia, setPlayContext]);

  if (!user) {
    return null;
  }

  return (
    <div className="full-screen-content">
      {isLoading && <div>Loading...</div>}
      {!currentItem && (
        <div className="flex grow shrink basis-full items-center justify-center gap-8 px-6">
          <Image
            src="/no-content.png"
            width={250}
            height={250}
            alt={'no-content'}
            className="aspect-square object-cover"
          />
          <p className="text-2xl font-bold text-center">
            {formatMessage({ id: 'flowScreen.noContent.title' })}
          </p>
          <p className="text-md text-center">
            {formatMessage({ id: 'flowScreen.noContent.subtitle' })}
          </p>
        </div>
      )}
      {currentItem && (
        <div
          className="flex grow shrink basis-full bg-cover bg-center"
          style={{
            backgroundImage: `url(${currentItem.cover})`,
          }}
        >
          <div className="flex flex-col justify-center items-center gap-[40px] md:gap-[180px] grow shrink-0 basis-full bg-[#00000099] backdrop-blur-xl">
            <div className="flex flex-col md:flex-row justify-center items-center w-full flex-initial gap-[40px]">
              <Image
                src={currentItem.cover}
                width={434}
                height={434}
                alt={currentItem.title}
                className="aspect-square object-cover rounded-lg"
              />
              <div className="flex flex-col items-center md:items-start gap-[42px] w-[90%] md:w-[40%]">
                <div>
                  <ProgramFollow programUUID={currentItem.programUUID} />
                </div>
                <div className="w-full flex flex-col gap-[8px] font-extrabold">
                  <p className="text-center md:text-start text-2xl">{currentItem.title}</p>
                  <div className="flex flex-row justify-center md:justify-start gap-2">
                    <Link href={currentItem.route}>{currentItem.program?.title ?? '?'}</Link>
                    <span>-</span>
                    <span>
                      {formatMessage(
                        { id: 'programScreen.information.medias.count' },
                        {
                          length: currentItem?.program?.mediasCount ?? 0,
                        }
                      )}
                    </span>
                  </div>
                </div>
                <div className="w-full">{playContext === 'flow' && <ProgressBar />}</div>
              </div>
            </div>
            <div className="flex items-center flex-initial gap-8 py-2 px-4 w-fit bg-[#000000] rounded-full">
              <BackForward direction="backward" />
              <PlayPause inFlow />
              <BackForward direction="forward" />
              <Next />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowPage;
